import { Copy } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  BriefingRequiredNextBanner,
  BriefingStepList,
  findFirstIncompleteRequiredStep,
  type BriefingStep,
} from '@/shared/briefing'
import {
  buildComponentRohsBriefing,
  type ComponentRohsBriefingStep,
  type ComponentRohsBriefingStepKey,
} from '@/shared/briefing/buildComponentRohsBriefing'
import { useGetCatalogRohsComponentComplianceFile } from '@/shared/hooks/api/rohsComplianceFiles'
import { isRohsTestReportAuditStale } from '@/shared/rohs/isRohsTestReportAuditStale'
import { rohsTestReportAuditRefetchInterval } from './rohsTestReportAuditRefetchInterval'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Alert, AlertAction, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { V2_COMPONENT_ROHS_BRIEFING_ID_PREFIX } from './constants'
import { V2ComponentRohsDrawingsStep } from './V2ComponentRohsDrawingsStep'
import { V2ComponentRohsSignOffStep } from './V2ComponentRohsSignOffStep'
import { V2ComponentRohsTestReportStep } from './V2ComponentRohsTestReportStep'
import { V2ComponentRohsWaitingStep } from './V2ComponentRohsWaitingStep'
import { V2ComponentRohsProgressBadge } from './V2ComponentRohsProgressBadge'
import type { CatalogComponentRohsFileListSummary } from '@/shared/hooks/api/catalog'

function toV2ComponentBriefingStep(
  step: Omit<
    ComponentRohsBriefingStep,
    'title' | 'description' | 'requirednessLabel'
  >,
  translate: ReturnType<typeof useTranslation>['t']
): ComponentRohsBriefingStep {
  return {
    ...step,
    title: translate(
      `v2.pages.components.detail.rohsBriefing.steps.${step.key}.title`
    ),
    description: translate(
      `v2.pages.components.detail.rohsBriefing.steps.${step.key}.description`
    ),
    requirednessLabel: translate(
      `v2.pages.components.detail.rohsBriefing.requiredness.${step.requiredness}`
    ),
  }
}

type V2ComponentRohsBriefingProps = {
  componentId: string
  canFulfil: boolean
  rohsFileSummary: CatalogComponentRohsFileListSummary
  supplierMagicLinkUrl: string | null
  provisionedBecauseText: string
}

export function V2ComponentRohsBriefing({
  componentId,
  canFulfil,
  rohsFileSummary,
  supplierMagicLinkUrl,
  provisionedBecauseText,
}: V2ComponentRohsBriefingProps) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const { data: file, isLoading } = useGetCatalogRohsComponentComplianceFile(
    componentId,
    {
      enabled: canFulfil,
      refetchInterval: rohsTestReportAuditRefetchInterval,
    }
  )

  const auditInProgress = file != null && isRohsTestReportAuditStale(file)

  const briefing = useMemo(
    () =>
      buildComponentRohsBriefing({
        canFulfil,
        file: file ?? null,
      }),
    [canFulfil, file]
  )

  const stepsWithLabels = useMemo(
    () => briefing.steps.map((step) => toV2ComponentBriefingStep(step, t)),
    [briefing.steps, t]
  )

  const stepsByKey = useMemo(() => {
    const keyed: Partial<
      Record<ComponentRohsBriefingStepKey, ComponentRohsBriefingStep>
    > = {}
    for (const step of stepsWithLabels) {
      keyed[step.key] = step
    }
    return keyed
  }, [stepsWithLabels])

  const firstIncompleteRequiredStep = useMemo(
    () => findFirstIncompleteRequiredStep(briefing.steps),
    [briefing.steps]
  )

  const firstIncompleteRequiredStepWithLabels =
    useMemo((): BriefingStep | null => {
      if (firstIncompleteRequiredStep == null) {
        return null
      }
      return toV2ComponentBriefingStep(firstIncompleteRequiredStep, t)
    }, [firstIncompleteRequiredStep, t])

  async function copySupplierLink(magicLinkUrl: string) {
    await navigator.clipboard.writeText(magicLinkUrl)
    toast.success(
      t(
        'v2.pages.products.detail.rohsFiles.componentFile.copySupplierLinkSuccess'
      )
    )
  }

  if (canFulfil && isLoading) {
    return (
      <div className='space-y-3' aria-busy='true'>
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }

  if (canFulfil && file == null) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.rohsFiles.componentFile.loadFailed')}
      </p>
    )
  }

  const incompleteMessage =
    firstIncompleteRequiredStepWithLabels != null
      ? t('v2.pages.components.detail.rohsBriefing.requiredNext.stepMessage', {
          stepTitle: firstIncompleteRequiredStepWithLabels.title,
        })
      : ''

  const goToStepLabel =
    firstIncompleteRequiredStepWithLabels != null
      ? t('v2.pages.components.detail.rohsBriefing.requiredNext.goToStep', {
          stepTitle: firstIncompleteRequiredStepWithLabels.title,
        })
      : ''

  return (
    <div className='space-y-6'>
      <p className='text-sm text-muted-foreground'>{provisionedBecauseText}</p>

      {canFulfil ? (
        <Alert variant='info'>
          <AlertDescription>
            <p>
              {t(
                'v2.pages.products.detail.rohsFiles.componentFile.filesProviderHelper'
              )}
            </p>
          </AlertDescription>
          {supplierMagicLinkUrl != null ? (
            <AlertAction>
              <Button
                className='gap-2'
                onClick={() => {
                  void copySupplierLink(supplierMagicLinkUrl)
                }}
                size='sm'
                type='button'
                variant='outline'
              >
                <Copy className='size-4' />
                {t(
                  'v2.pages.products.detail.rohsFiles.componentFile.copySupplierLink'
                )}
              </Button>
            </AlertAction>
          ) : null}
        </Alert>
      ) : null}

      {canFulfil ? (
        <BriefingRequiredNextBanner
          idPrefix={V2_COMPONENT_ROHS_BRIEFING_ID_PREFIX}
          title={t('v2.pages.components.detail.rohsBriefing.requiredNext.title')}
          allCompleteMessage={t(
            'v2.pages.components.detail.rohsBriefing.requiredNext.allComplete'
          )}
          incompleteMessage={incompleteMessage}
          goToStepLabel={goToStepLabel}
          firstIncompleteStep={firstIncompleteRequiredStepWithLabels}
        />
      ) : null}

      <BriefingStepList
        title={t('v2.pages.components.detail.rohsBriefing.title')}
        description={t('v2.pages.components.detail.rohsBriefing.description')}
        titleAccessory={
          <V2ComponentRohsProgressBadge rohsFile={rohsFileSummary} />
        }
      >
        {!canFulfil && stepsByKey.waiting != null ? (
          <V2ComponentRohsWaitingStep step={stepsByKey.waiting} index={0} />
        ) : null}
        {canFulfil && file != null && stepsByKey.testReport != null ? (
          <V2ComponentRohsTestReportStep
            step={stepsByKey.testReport}
            index={0}
            componentId={componentId}
            file={file}
            canWrite={canWrite}
            auditInProgress={auditInProgress}
            auditScope='catalog'
          />
        ) : null}
        {canFulfil && file != null && stepsByKey.drawings != null ? (
          <V2ComponentRohsDrawingsStep
            step={stepsByKey.drawings}
            index={1}
            componentId={componentId}
            file={file}
            canWrite={canWrite}
          />
        ) : null}
        {canFulfil && file != null && stepsByKey.signOff != null ? (
          <V2ComponentRohsSignOffStep
            step={stepsByKey.signOff}
            index={2}
            componentId={componentId}
            file={file}
          />
        ) : null}
      </BriefingStepList>
    </div>
  )
}
